import { useState } from "react";
import { Select, MenuItem, FormControl, InputLabel, Button, TextField } from "@mui/material";
import { Close } from "@mui/icons-material";

/**
 * OrganizationSearch component
 *
 * Shows search/filter controls and a dynamic result list based on name, department, and area.
 *
 * @param {Object} props
 * @param {Object} props.originalData - Root of the original org chart.
 * @param {Function} props.onSelectTree - Callback with the updated subtree.
 */
export default function OrganizationSearch({ originalData, onSelectTree }) {
  const [search, setSearch] = useState("");
  const [selectedPerson, setSelectedPerson] = useState(null);
  // const [data, setData] = useState(originalData);
  const [selectedArea, setSelectedArea] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  function normalize(text) {
    return text
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .trim();
  }

  function countSubordinates(node) {
    if (!node.children || node.children.length === 0) return 0;
    return node.children.reduce(
      (acc, child) => acc + 1 + countSubordinates(child),
      0
    );
  }

  function collectPeople(node, people = []) {
    if (node.name?.trim()) {
      people.push({
        id: node.id,
        name: node.name.trim(),
        position: node.position || "",
        profile_picture: node.profile_picture || null,
        department: node.department || "",
        full_department: node.full_department || "",
        area: node.area || "",
        reports: countSubordinates(node),
      });
    }
    for (let child of node.children || []) {
      collectPeople(child, people);
    }
    return people;
  }

  function findNodeById(node, id) {
    if (node.id === id) return node;
    for (let child of node.children || []) {
      const found = findNodeById(child, id);
      if (found) return found;
    }
    return null;
  }

  function findParent(root, childId) {
    let parent = null;
    function recurse(node) {
      for (let child of node.children || []) {
        if (child.id === childId) {
          parent = node;
          return;
        }
        recurse(child);
      }
    }
    recurse(root);
    return parent;
  }

  function deepClone(node) {
    return {
      ...node,
      children: (node.children || []).map(deepClone),
    };
  }

  function filterTreeByHierarchy(node, area, department) {
    const matchesArea = !area || node.area === area;
    const matchesDepartment = !department || node.full_department === department;

    const filteredChildren = (node.children || [])
      .map(node => filterTreeByHierarchy(node, area, department))
      .filter(Boolean);

    if ((matchesArea && matchesDepartment) || filteredChildren.length > 0) {
      return {
        ...node,
        children: filteredChildren,
      };
    }

    return null;
  }

  const handleSelect = (id) => {
    const foundNode = findNodeById(originalData, id);
    if (!foundNode) return;

    const parent = findParent(originalData, foundNode.id);
    const selectedClone = deepClone(foundNode);

    if (parent) {
      selectedClone.parent = {
        id: parent.id,
        name: parent.name,
        position: parent.position,
        profile_picture: parent.profile_picture || null,
      };
    }

    const newTree = selectedClone;
    // setData(newTree);
    onSelectTree?.(newTree);
    setSearch("");
    setSelectedPerson({
      id: foundNode.id,
      name: foundNode.name,
      position: foundNode.position,
      profile_picture: foundNode.profile_picture,
      reports: countSubordinates(foundNode),
    });
  };

  const handleClearSelection = () => {
    // setData(originalData);
    onSelectTree?.(originalData);
    setSelectedPerson(null);
    setSearch("");
    setSelectedArea("");
    setSelectedDepartment("");
  };

  const allPeople = collectPeople(originalData);
  const filteredByArea = selectedArea
    ? allPeople.filter((p) => p.area === selectedArea)
    : allPeople;
  const filteredByDepartment = selectedDepartment
    ? filteredByArea.filter((p) => p.full_department === selectedDepartment)
    : filteredByArea;

  const normalizedSearch = normalize(search);
  const filteredPeopleRaw = filteredByDepartment.filter((person) => {
    return (
      normalize(person.name).includes(normalizedSearch) ||
      normalize(person.position).includes(normalizedSearch)
    );
  });

  const seen = new Set();
  const filteredPeople = filteredPeopleRaw.filter((p) => {
    const key = `${p.name}-${p.position}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const areaOptions = [...new Set(allPeople.map((p) => p.area).filter(Boolean))];
  const departmentOptions = [...new Set(filteredByArea.map((p) => p.full_department).filter(Boolean))];

  const applyTreeFilter = ({ selectedArea, selectedDepartment }) => {
    const filtered = filterTreeByHierarchy(originalData, selectedArea, selectedDepartment);
    // setData(filtered || originalData);
    onSelectTree?.(filtered || originalData);
  };

  return (
    <div style={{ width: "100%", minWidth: 500, margin: "0", position: "relative", display: "flex", alignItems: "center", gap: 10 }}>
      <FormControl style={{width:150}} size="small">
        <InputLabel>Área</InputLabel>
        <Select
          value={selectedArea}
          label="Área"
          onChange={(e) => {
            const value = e.target.value;
            if(!value) return handleClearSelection()
            setSelectedArea(value);
            setSelectedDepartment("");
            applyTreeFilter({selectedArea: value});
          }}
        >
          <MenuItem value="">Todas</MenuItem>
          {areaOptions.map((area) => (
            <MenuItem key={area} value={area}>
              {area}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" style={{width:150}}>
        <InputLabel>Departamento</InputLabel>
        <Select
          value={selectedDepartment}
          label="Departamento"
          onChange={(e) => {
            const value = e.target.value;
            if(!value) return handleClearSelection()
            setSelectedDepartment(value);
            applyTreeFilter({ selectedDepartment: value });
          }}
        >
          <MenuItem value="">Todos</MenuItem>
          {departmentOptions.map((dept) => (
            <MenuItem key={dept} value={dept}>
              {dept}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div style={{position: "relative"}}>
      {!selectedPerson ? (
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          label="Buscar por nombre..."
          size="small"
          style={{
            width: 200,
          }}
        />
      ) : (
        <div
          style={{
            border: "1px solid #ccc",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            padding: "10px",
            gap: "10px",
            borderRadius: 6,
          }}
        >
          <img
            src={
              selectedPerson.profile_picture ||
              "https://via.placeholder.com/40?text=👤"
            }
            alt={selectedPerson.name}
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: "bold" }}>{selectedPerson.name}</span>
            <span
              style={{
                fontSize: "12px",
                color: "#666",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                maxWidth: 170,
                display: "block",
              }}
            >
              {selectedPerson.position}
            </span>
            {selectedPerson.reports > 0 && (
              <div style={{ fontSize: "12px", color: "#999" }}>
                👥 {selectedPerson.reports} persona
                {selectedPerson.reports !== 1 ? "s a cargo" : " a cargo"}
              </div>
            )}
          </div>
          <button
            onClick={handleClearSelection}
            style={{
              padding: "4px 8px",
              backgroundColor: "#eee",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "12px",
              height: "30px",
            }}
          >
            Limpiar
          </button>
        </div>
      )}

      {search && (
        <ul
          style={{
            maxHeight: "300px",
            overflowY: "auto",
            border: "1px solid #ccc",
            marginTop: "8px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
            padding: 0,
            listStyle: "none",
            zIndex: 10,
            position: "absolute",
            borderRadius: 8,
            right: 0,
            width: "max-content",
            maxWidth: 400,
            overflow: "hidden"
          }}
        >
          {filteredPeople.length > 0 ? (
            filteredPeople.map((person) => (
              <li
                key={person.id}
                onClick={() => handleSelect(person.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "10px",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                  width: "100%",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f7f7f7")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "white")
                }
              >
                <img
                  src={
                    person.profile_picture ||
                    "https://via.placeholder.com/40?text=👤"
                  }
                  alt={person.name}
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    marginRight: "10px",
                  }}
                />
                <div style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <span style={{ fontWeight: "bold" }}>{person.name}</span>
                  <span style={{ fontSize: "12px", color: "#666", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }}>
                    {person.position}
                  </span>
                  {person.reports > 0 && (
                    <span style={{ fontSize: "12px", color: "#999" }}>
                      👥 {person.reports} persona
                      {person.reports !== 1 ? "s a cargo" : " a cargo"}
                    </span>
                  )}
                </div>
              </li>
            ))
          ) : (
            <li style={{ padding: "10px", color: "#999" }}>
              No se encontraron resultados
            </li>
          )}
        </ul>
      )}
      </div>

        {
        (selectedArea || selectedDepartment || selectedPerson) &&
        <Button onClick={handleClearSelection} type="submit" variant="contained" color="primary" style={{width: 150, gap: 5}}><Close />Limpiar filtros</Button>
      }
    </div>
  );
}
